'use client'
import {
  useGetCookieConsentQuery,
  useGetUpdateCookieConsentMutation,
} from '@/app/globalRedux/api/constantSlice'
import { RootState } from '@/app/globalRedux/store'
import { useTranslation } from '@/app/i18n/client'
import {
  CookieConsentResponseData,
  CookieContextType,
  CookiePreferences,
  postCookieType,
} from '@/app/types/cookieConsentType'
import {
  applyPreferences,
  removeGACookies,
  setBasicCookies,
  setRecaptchaCookie,
  setThirdPartyCookie,
  unSetRecaptchaCookie,
  unSetThirdPartyCookie,
} from '@/app/utils/cookiesConsent'
import { getCookies } from '@/lib/utils'
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'sonner'
const CookieContext = createContext<CookieContextType | undefined>(undefined)

export const cookiesInitialState: CookiePreferences = {
  necessary: true,
  thirdParty: getCookies()?.third_party === 'enabled' || false,
  recaptcha: getCookies()?.recaptcha === 'enabled' || false,
}

export const CookieProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation()
  const user = useSelector((state: RootState) => state.user)
  const cookies = getCookies()
  const isLoggedIn = user?.loggedIn ?? false
  const [updateCookieConsent] = useGetUpdateCookieConsentMutation()
  const { data: cookieConsentData } = useGetCookieConsentQuery<{
    data: CookieConsentResponseData
  }>(undefined, { skip: !isLoggedIn })

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [showDetailedView, setShowDetailedView] = useState(false)
  const [isNecessarySectionExpanded, setIsNecessarySectionExpanded] =
    useState(true)
  const [isThirdPartySectionExpanded, setIsThirdPartySectionExpanded] =
    useState(false)
  const [isRecaptchaSectionExpanded, setIsRecaptchaSectionExpanded] =
    useState(false)
  const [cookiePreferences, setCookiePreferences] =
    useState<CookiePreferences>(cookiesInitialState)

  // For manual trigger update
  const postCookies = async ({ thirdParty, recaptcha }: postCookieType) => {
    setBasicCookies()
    if (!isLoggedIn) {
      const { shouldReload: thirdPartyReload } = applyPreferences({
        actualValue: thirdParty,
        storedCookie: cookies?.third_party,
        cleanupFunction: removeGACookies,
        setterFunction: setThirdPartyCookie,
        unSetterFunction: unSetThirdPartyCookie,
      })

      const { shouldReload: recaptchaReload } = applyPreferences({
        actualValue: recaptcha,
        storedCookie: cookies?.recaptcha,
        cleanupFunction: () => {},
        setterFunction: setRecaptchaCookie,
        unSetterFunction: unSetRecaptchaCookie,
      })
      setIsModalOpen(false)
      if (thirdPartyReload || recaptchaReload) {
        window.location.reload()
      }
      return
    }

    try {
      const postCookiesResponse = await updateCookieConsent({
        thirdParty: thirdParty,
        recaptcha: recaptcha,
      }).unwrap()

      if (postCookiesResponse?.status === 'success') {
        toast.success(t('cookie_success'))
        setIsModalOpen(false)
        window.location.reload()
      }
    } catch (error: any) {
      console.error('Error occurred while updating cookies:', error)
    }
  }

  const applyPreferenceToSystem = ({
    thirdParty,
    recaptcha,
  }: postCookieType) => {
    const cookies = getCookies()
    setBasicCookies()

    const { shouldReload: thirdPartyReload } = applyPreferences({
      actualValue: thirdParty,
      storedCookie: cookies?.third_party,
      cleanupFunction: removeGACookies,
      setterFunction: setThirdPartyCookie,
      unSetterFunction: unSetThirdPartyCookie,
    })

    const { shouldReload: recaptchaReload } = applyPreferences({
      actualValue: recaptcha,
      storedCookie: cookies?.recaptcha,
      cleanupFunction: () => {},
      setterFunction: setRecaptchaCookie,
      unSetterFunction: unSetRecaptchaCookie,
    })

    if (thirdPartyReload || recaptchaReload) {
      window.location.reload()
    }
  }

  useEffect(() => {
    if (!cookieConsentData || cookieConsentData.status !== 'success') return

    const expired = cookieConsentData.cookie_consent?.expired
    const thirdPartyPref = cookieConsentData.cookie_consent?.googleAnalytics
    const recaptchaValue = cookieConsentData.cookie_consent?.recaptcha

    if (expired) {
      setIsModalOpen(true)
    } else {
      setIsModalOpen(false)
    }

    setCookiePreferences({
      necessary: true,
      thirdParty: thirdPartyPref,
      recaptcha: recaptchaValue,
    })
    applyPreferenceToSystem({
      thirdParty: thirdPartyPref,
      recaptcha: recaptchaValue,
    })
  }, [cookieConsentData])

  useEffect(() => {
    if (cookies?.basic_cookie !== 'enabled') {
      setIsModalOpen(true)
      return
    }
  }, [cookies])

  useEffect(() => {
    if (!isLoggedIn && cookies?.third_party === 'disabled') {
      removeGACookies()
    }
  }, [])

  return (
    <CookieContext.Provider
      value={{
        isModalOpen,
        showDetailedView,
        cookiePreferences,
        setCookiePreferences,
        isNecessarySectionExpanded,
        isThirdPartySectionExpanded,
        isRecaptchaSectionExpanded,
        setIsModalOpen,
        setShowDetailedView,
        setIsNecessarySectionExpanded,
        setIsThirdPartySectionExpanded,
        postCookies,
        setIsRecaptchaSectionExpanded,
      }}
    >
      {children}
    </CookieContext.Provider>
  )
}

// Custom Hook for Context Usage
export const useCookieContext = () => {
  const context = useContext(CookieContext)
  if (!context) {
    throw new Error('useCookieContext must be used within a CookieProvider')
  }
  return context
}
